import { useEffect, useRef, useState } from "react";

const LONG_PRESS_MS = 300;
const SLOW_PLAYBACK_RATE = 0.5;
const LONG_PRESS_HINT_KEY = "lexivision:sign-player-long-press-hint-shown";

export type SignWord =
  | { matched: true; label: string; webmUrl: string; group?: string }
  | { matched: false; label: string; group?: string };

type SignPlayerProps = {
  words: SignWord[];
  /** 「▶ 連続再生」ボタンを出すか。単語1つだけの画面では不要なので false にする */
  showSequenceControl?: boolean;
};

type WordCluster = {
  group?: string;
  items: { word: SignWord; index: number }[];
};

/**
 * group が同じまま連続する単語を1つのクラスタにまとめる。
 * 「動物」「場所」のように辞書に無い複合語（動物園）を分解表現した単語群を、
 * UI上でまとめて表示するために使う。
 */
const clusterWords = (words: SignWord[]): WordCluster[] => {
  const clusters: WordCluster[] = [];
  for (const [index, word] of words.entries()) {
    const last = clusters.at(-1);
    if (last && word.group !== undefined && last.group === word.group) {
      last.items.push({ word, index });
      continue;
    }
    clusters.push({
      ...(word.group ? { group: word.group } : {}),
      items: [{ word, index }],
    });
  }
  return clusters;
};

export function SignPlayer({
  words,
  showSequenceControl = true,
}: SignPlayerProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(
    words.findIndex((w) => w.matched),
  );
  const [isPlayingSequence, setIsPlayingSequence] = useState(false);
  // activeIndex が同じ値のまま再クリックされても再生し直せるよう、
  // クリックのたびにトークンを進めて useEffect を強制的に再実行させる。
  const [playToken, setPlayToken] = useState(0);
  const [isSlowMode, setIsSlowMode] = useState(false);
  const [showLongPressHint] = useState(() => {
    const alreadyShown = localStorage.getItem(LONG_PRESS_HINT_KEY) === "true";
    if (!alreadyShown) {
      localStorage.setItem(LONG_PRESS_HINT_KEY, "true");
    }
    return !alreadyShown;
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const pressTimerRef = useRef<number | null>(null);
  const wasLongPressRef = useRef(false);

  const activeWord =
    activeIndex !== null && activeIndex >= 0 ? words[activeIndex] : undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeWord は activeIndex から導出されるため依存に含めない
  useEffect(() => {
    if (activeWord?.matched && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [activeIndex, playToken]);

  // 単語切り替えとは独立に再生速度だけを変更できるようにするため、
  // load()/play() を呼ぶ上の useEffect とは分離している。
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = isSlowMode ? SLOW_PLAYBACK_RATE : 1;
    }
  }, [isSlowMode]);

  const playFromStart = () => {
    const firstMatchedIndex = words.findIndex((w) => w.matched);
    setActiveIndex(firstMatchedIndex === -1 ? null : firstMatchedIndex);
    setIsPlayingSequence(true);
    setPlayToken((t) => t + 1);
  };

  const onVideoEnded = () => {
    if (!isPlayingSequence || activeIndex === null) return;
    let nextIndex = activeIndex + 1;
    while (nextIndex < words.length && !words[nextIndex]?.matched) {
      nextIndex++;
    }
    if (nextIndex < words.length) {
      setActiveIndex(nextIndex);
    } else {
      setIsPlayingSequence(false);
    }
  };

  const onCardTap = (index: number) => {
    const word = words[index];
    if (!word?.matched) return;
    setIsPlayingSequence(false);
    setActiveIndex(index);
    setPlayToken((t) => t + 1);
  };

  const clearPressTimer = () => {
    if (pressTimerRef.current !== null) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  // 指を置いてから LONG_PRESS_MS 経過してもまだ押されていれば長押しとみなし、
  // その場でスロー再生を開始する（放すのを待たない）。
  const onCardPointerDown = (index: number) => {
    const word = words[index];
    if (!word?.matched) return;
    wasLongPressRef.current = false;
    clearPressTimer();
    pressTimerRef.current = window.setTimeout(() => {
      wasLongPressRef.current = true;
      onCardTap(index);
      setIsSlowMode(true);
    }, LONG_PRESS_MS);
  };

  const onCardPointerEnd = () => {
    clearPressTimer();
    setIsSlowMode(false);
  };

  // 長押し成立後は pointerup に続いて click も発火するため、
  // 長押し由来の click では再生を二重に開始しないようガードする。
  const onCardClick = (index: number) => {
    if (wasLongPressRef.current) {
      wasLongPressRef.current = false;
      return;
    }
    onCardTap(index);
  };

  return (
    <div className="space-y-4">
      <div className="mx-auto w-[300px] h-[500px] rounded-2xl bg-neutral-900 overflow-hidden flex items-center justify-center">
        {activeWord?.matched ? (
          <video
            ref={videoRef}
            src={activeWord.webmUrl}
            className="w-full h-full object-contain"
            playsInline
            muted
            onEnded={onVideoEnded}
          />
        ) : (
          <p className="text-neutral-400 text-sm">
            対応する手話が見つかりませんでした
          </p>
        )}
      </div>

      {showLongPressHint && (
        <p className="text-center text-xs text-neutral-400">
          長押しでゆっくり再生
        </p>
      )}

      <div className="flex items-start gap-2 overflow-x-auto pb-2">
        {clusterWords(words).map((cluster, clusterIndex) => (
          <div
            key={`cluster-${clusterIndex}-${cluster.items[0]?.index}`}
            className={
              cluster.group
                ? "shrink-0 flex flex-col items-center gap-1 rounded-xl border border-dashed border-neutral-300 px-2 py-1.5"
                : "shrink-0 flex flex-col items-center gap-1"
            }
          >
            <div className="flex gap-1">
              {cluster.items.map(({ word, index }) => (
                <button
                  key={`${word.label}-${index}`}
                  type="button"
                  onClick={() => onCardClick(index)}
                  onPointerDown={() => onCardPointerDown(index)}
                  onPointerUp={onCardPointerEnd}
                  onPointerLeave={onCardPointerEnd}
                  disabled={!word.matched}
                  className={`shrink-0 rounded-xl px-4 py-3 text-sm font-medium transition-colors select-none [-webkit-touch-callout:none] ${
                    index === activeIndex
                      ? "bg-emerald-500 text-white"
                      : word.matched
                        ? "bg-neutral-800 text-white hover:bg-neutral-700"
                        : "bg-neutral-100 text-neutral-400"
                  }`}
                >
                  {word.label}
                </button>
              ))}
            </div>
            {cluster.group && (
              <p className="text-xs text-neutral-400">({cluster.group})</p>
            )}
          </div>
        ))}
      </div>

      {showSequenceControl && (
        <button
          type="button"
          onClick={playFromStart}
          disabled={!words.some((w) => w.matched)}
          className="w-full rounded-xl bg-neutral-900 py-3 text-white font-semibold disabled:opacity-40 hover:bg-neutral-700 transition-colors"
        >
          ▶ 連続再生
        </button>
      )}
    </div>
  );
}
